/**
 * Leviathan Web - Email Templates Page
 * Quasar Prime LLC
 *
 * Manage email templates for invoices, statements, deliveries, etc.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  Mail,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  FileText,
  CheckCircle,
  XCircle,
  Shield,
  Copy,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface EmailTemplate {
  id: string;
  code: string;
  name: string;
  description: string | null;
  subject: string;
  htmlBody: string;
  textBody: string | null;
  isActive: boolean;
  isSystem: boolean;
  category: string;
  createdAt: string;
  updatedAt: string;
}

const CATEGORY_CONFIG: Record<string, { label: string; color: string }> = {
  INVOICE: { label: 'Invoice', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  STATEMENT: { label: 'Statement', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  DELIVERY: { label: 'Delivery', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  ORDER: { label: 'Order', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  NOTIFICATION: { label: 'Notification', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
  CUSTOM: { label: 'Custom', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
};

export default function EmailTemplates() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { accessToken } = useAuth();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  // Fetch templates
  const { data: templates, isLoading } = useQuery({
    queryKey: ['email-templates'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/email/templates`, { headers });
      if (!response.ok) throw new Error('Failed to fetch templates');
      return response.json() as Promise<EmailTemplate[]>;
    },
    enabled: !!accessToken,
  });

  // Initialize default templates
  const initializeMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_URL}/api/email/templates/initialize`, {
        method: 'POST',
        headers,
      });
      if (!response.ok) throw new Error('Failed to initialize templates');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
  });

  // Toggle template active status
  const toggleMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const response = await fetch(`${API_URL}/api/email/templates/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ isActive }),
      });
      if (!response.ok) throw new Error('Failed to update template');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
  });

  // Delete template
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`${API_URL}/api/email/templates/${id}`, {
        method: 'DELETE',
        headers,
      });
      if (!response.ok) throw new Error('Failed to delete template');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      setDeleteId(null);
    },
  });

  // Duplicate template
  const duplicateMutation = useMutation({
    mutationFn: async (template: EmailTemplate) => {
      const response = await fetch(`${API_URL}/api/email/templates`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          code: `${template.code}_COPY`,
          name: `${template.name} (Copy)`,
          description: template.description,
          subject: template.subject,
          htmlBody: template.htmlBody,
          textBody: template.textBody,
          category: template.category,
          isActive: false,
        }),
      });
      if (!response.ok) throw new Error('Failed to duplicate template');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
  });

  // Group templates by category
  const groupedTemplates = templates?.reduce(
    (acc, template) => {
      const category = template.category || 'CUSTOM';
      if (!acc[category]) acc[category] = [];
      acc[category].push(template);
      return acc;
    },
    {} as Record<string, EmailTemplate[]>
  );

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center bg-amber-500/20 border border-amber-500/30">
            <Mail className="h-5 w-5 text-amber-500" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Email Templates</h1>
            <p className="text-sm text-slate-400">
              Manage email templates for automated communications
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => initializeMutation.mutate()}
            disabled={initializeMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 border border-slate-600 text-slate-300 hover:bg-slate-600 transition-colors disabled:opacity-50"
          >
            <RefreshCw
              className={`h-4 w-4 ${initializeMutation.isPending ? 'animate-spin' : ''}`}
            />
            Initialize Defaults
          </button>
          <button
            onClick={() => navigate('/settings/email/templates/new')}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white hover:bg-amber-500 transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Template
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 text-amber-500 animate-spin" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && (!templates || templates.length === 0) && (
        <div className="bg-slate-800/50 border border-slate-700 p-12 text-center">
          <FileText className="h-12 w-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No Templates Found</h3>
          <p className="text-slate-400 mb-6">
            Get started by initializing the default templates or creating a custom one.
          </p>
          <button
            onClick={() => initializeMutation.mutate()}
            disabled={initializeMutation.isPending}
            className="px-6 py-2 bg-amber-600 text-white hover:bg-amber-500 transition-colors"
          >
            Initialize Default Templates
          </button>
        </div>
      )}

      {/* Templates Grid by Category */}
      {!isLoading && groupedTemplates && Object.keys(groupedTemplates).length > 0 && (
        <div className="space-y-8">
          {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => {
            const config = CATEGORY_CONFIG[category] ?? CATEGORY_CONFIG.CUSTOM!;
            return (
              <div key={category}>
                <div className="flex items-center gap-3 mb-4">
                  <span
                    className={`px-3 py-1 text-xs font-medium border ${config!.color}`}
                  >
                    {config!.label}
                  </span>
                  <span className="text-sm text-slate-500">
                    {categoryTemplates.length} template
                    {categoryTemplates.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categoryTemplates.map((template) => (
                    <div
                      key={template.id}
                      className="bg-slate-800/50 border border-slate-700 hover:border-slate-600 transition-colors"
                    >
                      {/* Template Header */}
                      <div className="p-4 border-b border-slate-700">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-white font-medium truncate">
                                {template.name}
                              </h3>
                              {template.isSystem && (
                                <Shield className="h-4 w-4 text-blue-400 flex-shrink-0" />
                              )}
                            </div>
                            <code className="text-xs text-slate-500 font-mono">
                              {template.code}
                            </code>
                          </div>
                          <button
                            onClick={() =>
                              toggleMutation.mutate({
                                id: template.id,
                                isActive: !template.isActive,
                              })
                            }
                            className={`flex-shrink-0 p-1 rounded transition-colors ${
                              template.isActive
                                ? 'text-green-400 hover:bg-green-500/20'
                                : 'text-slate-500 hover:bg-slate-700'
                            }`}
                            title={template.isActive ? 'Active' : 'Inactive'}
                          >
                            {template.isActive ? (
                              <CheckCircle className="h-5 w-5" />
                            ) : (
                              <XCircle className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                        {template.description && (
                          <p className="text-sm text-slate-400 line-clamp-2">
                            {template.description}
                          </p>
                        )}
                      </div>

                      {/* Template Subject Preview */}
                      <div className="px-4 py-3 bg-slate-900/50">
                        <div className="text-xs text-slate-500 mb-1">Subject:</div>
                        <div className="text-sm text-slate-300 truncate">
                          {template.subject}
                        </div>
                      </div>

                      {/* Template Actions */}
                      <div className="p-3 flex items-center justify-between border-t border-slate-700">
                        <div className="text-xs text-slate-500">
                          Updated{' '}
                          {new Date(template.updatedAt).toLocaleDateString()}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => duplicateMutation.mutate(template)}
                            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                            title="Duplicate"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() =>
                              navigate(`/settings/email/templates/${template.id}`)
                            }
                            className="p-2 text-slate-400 hover:text-amber-500 hover:bg-slate-700 transition-colors"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          {!template.isSystem && (
                            <button
                              onClick={() => setDeleteId(template.id)}
                              className="p-2 text-slate-400 hover:text-red-500 hover:bg-slate-700 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 border border-slate-700 p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-white mb-2">Delete Template</h3>
            <p className="text-slate-400 mb-6">
              Are you sure you want to delete this template? This action cannot be
              undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteId(null)}
                className="px-4 py-2 bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteId)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-500 transition-colors disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
